import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, X, MapPin, Phone, Instagram, Facebook, ShoppingBag, ArrowRight } from 'lucide-react';
import { useRestaurantCart } from '@happyspace/sdk/restaurant';
import { SITE } from '../data/site';
import { cn } from '../lib/util';
import { useCartUI } from '../lib/cartUi';

function CartButton({ className, dark = false }: { className?: string; dark?: boolean }) {
  const { openCart } = useCartUI();
  const { itemCount } = useRestaurantCart();
  return (
    <button onClick={openCart} className={cn('btn btn-primary relative', className)} style={{ padding: '0.6rem 1.25rem' }}>
      <ShoppingBag size={16} /> Order Online
      {itemCount > 0 && (
        <span
          className="tnum absolute -top-1.5 -right-1.5 grid place-items-center min-w-[1.15rem] h-[1.15rem] px-1 rounded-full text-[0.68rem] font-bold"
          style={{ background: dark ? '#fff' : 'var(--color-ink)', color: dark ? 'var(--color-ink)' : 'var(--color-ink-inverse)' }}
        >
          {itemCount}
        </span>
      )}
    </button>
  );
}

const LINKS = [
  { to: '/', label: 'Home' },
  { to: '/menu', label: 'Menu' },
  { to: '/about', label: 'Our Story' },
  { to: '/visit', label: 'Visit' },
];

const LOGO_URL = 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/8d073425-a70c-42c7-a3ed-1a2c0386029b/1783624268133-5j77qa.jpg';

function Wordmark({ onDark = false }: { onDark?: boolean }) {
  return (
    <Link to="/" className="group inline-flex items-center gap-2 shrink-0" aria-label="Parotta Express home">
      <img
        src={LOGO_URL}
        alt="Parotta Express logo"
        className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover shrink-0"
        style={{ border: onDark ? '1px solid rgba(255,255,255,0.35)' : '1px solid var(--color-line)' }}
      />
      <span
        className="font-display leading-none whitespace-nowrap text-base md:text-[1.2rem]"
        style={{ color: onDark ? 'var(--color-ink-inverse)' : 'var(--color-ink)' }}
      >
        Parotta <span style={{ color: 'var(--color-accent)' }}>Express</span>
      </span>
    </Link>
  );
}

export function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openCart } = useCartUI();
  const loc = useLocation();
  const overHero = loc.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const solid = scrolled || !overHero;
  const dark = !solid; // dark glass over the hero, light glass once scrolled / on inner pages

  return (
    <header
      className="fixed inset-x-0 top-0 z-50"
      style={{ paddingTop: solid ? '0.55rem' : '0.9rem', transition: 'padding 0.4s ease' }}
    >
      <div className="wrap">
        <nav
          className="flex items-center justify-between rounded-2xl pl-5 pr-3 md:pr-3.5"
          style={{
            height: solid ? '3.9rem' : '4.5rem',
            background: dark
              ? 'rgba(24,17,10,0.34)'
              : 'color-mix(in srgb, var(--color-surface) 82%, transparent)',
            backdropFilter: 'saturate(1.5) blur(16px)',
            WebkitBackdropFilter: 'saturate(1.5) blur(16px)',
            border: `1px solid ${dark ? 'rgba(255,255,255,0.16)' : 'var(--color-line)'}`,
            boxShadow: dark ? '0 8px 30px rgba(0,0,0,0.22)' : 'var(--pe-shadow)',
            transition: 'height 0.4s ease, background 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease',
          }}
        >
          <Wordmark onDark={dark} />

          <div className="hidden md:flex items-center gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className="navlink relative px-3.5 py-2 text-[0.95rem] font-medium rounded-full transition-colors duration-200"
                style={({ isActive }) => ({
                  color: isActive
                    ? dark ? '#fff' : 'var(--color-accent)'
                    : dark ? 'rgba(255,255,255,0.82)' : 'var(--color-ink-2)',
                })}
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <span className="absolute left-1/2 -bottom-1 h-[3px] w-5 -translate-x-1/2 rounded-full" style={{ background: 'var(--color-accent)' }} />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-4">
            <a href={SITE.phoneHref} className="text-[0.9rem] font-medium inline-flex items-center gap-1.5 transition-colors hover:opacity-80" style={{ color: dark ? 'rgba(255,255,255,0.88)' : 'var(--color-ink-2)' }}>
              <Phone size={15} /> {SITE.phone}
            </a>
            <Link to="/menu" className="btn btn-primary" style={{ padding: '0.6rem 1.25rem' }}>
              <ShoppingBag size={16} /> Order Online
            </Link>
          </div>

          <button className="md:hidden grid place-items-center w-11 h-11 rounded-xl transition-colors" aria-label="Open menu" onClick={() => setOpen(true)} style={{ color: dark ? '#fff' : 'var(--color-ink)', background: dark ? 'rgba(255,255,255,0.12)' : 'var(--color-card-2)' }}>
            <MenuIcon size={20} />
          </button>
        </nav>
      </div>

      {/* Mobile sheet */}
      <div className={cn('md:hidden fixed inset-0 z-50 transition-opacity duration-300', open ? 'opacity-100' : 'pointer-events-none opacity-0')}>
        <div className="absolute inset-0" style={{ background: 'var(--pe-scrim)' }} onClick={() => setOpen(false)} />
        <div
          className="absolute right-0 top-0 h-full w-[82%] max-w-sm p-7 flex flex-col transition-transform duration-400"
          style={{ background: 'var(--color-surface)', transform: open ? 'translateX(0)' : 'translateX(100%)', boxShadow: 'var(--pe-shadow-lg)' }}
        >
          <div className="flex items-center justify-between">
            <Wordmark />
            <button aria-label="Close menu" onClick={() => setOpen(false)} className="grid place-items-center w-10 h-10 rounded-full" style={{ background: 'var(--color-card-2)' }}>
              <X size={18} />
            </button>
          </div>
          <div className="mt-10 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} className={({ isActive }) => cn('font-display py-3 text-2xl border-b', isActive ? 'text-accent-deep' : 'text-ink')} style={{ borderColor: 'var(--color-line)' }}>
                {l.label}
              </NavLink>
            ))}
          </div>
          <Link to="/menu" className="btn btn-primary mt-6 justify-center w-full" style={{ padding: '0.85rem 1.25rem' }}>
            <ShoppingBag size={16} /> Order Online
          </Link>

          <div className="mt-auto pt-8 text-sm text-ink-2 space-y-2">
            <a href={SITE.address.mapsUrl} target="_blank" rel="noreferrer" className="flex items-start gap-2">
              <MapPin size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--color-accent)' }} />
              <span>{SITE.address.line1}, {SITE.address.line2}</span>
            </a>
            <a href={SITE.phoneHref} className="flex items-center gap-2">
              <Phone size={16} style={{ color: 'var(--color-accent)' }} /> {SITE.phone}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="relative overflow-hidden grain" style={{ background: 'var(--color-ink)', color: 'var(--color-ink-inverse)' }}>
      <div className="wrap relative py-16 md:py-20">
        <div className="grid gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <div className="flex items-baseline gap-[0.15em]">
              <span className="font-display text-2xl" style={{ color: 'var(--color-ink-inverse)' }}>Parotta</span>
              <span className="font-display text-2xl" style={{ color: 'var(--color-accent)' }}>Express</span>
            </div>
            <p className="mt-4 max-w-sm text-[0.98rem] leading-relaxed" style={{ color: 'color-mix(in srgb, var(--color-ink-inverse) 72%, transparent)' }}>
              {SITE.tagline}. Authentic South Indian parottas, dosas, biryanis and Chettinad curries — made fresh every day in {SITE.city}.
            </p>
            <div className="mt-6 flex gap-3">
              <a href={SITE.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="grid place-items-center w-10 h-10 rounded-full transition-colors" style={{ border: '1px solid color-mix(in srgb, var(--color-ink-inverse) 24%, transparent)' }}>
                <Facebook size={17} />
              </a>
              <a href={SITE.address.mapsUrl} target="_blank" rel="noreferrer" aria-label="Directions" className="grid place-items-center w-10 h-10 rounded-full transition-colors" style={{ border: '1px solid color-mix(in srgb, var(--color-ink-inverse) 24%, transparent)' }}>
                <MapPin size={17} />
              </a>
              <a href={SITE.social.facebook} target="_blank" rel="noreferrer" aria-label="Instagram" className="grid place-items-center w-10 h-10 rounded-full transition-colors" style={{ border: '1px solid color-mix(in srgb, var(--color-ink-inverse) 24%, transparent)' }}>
                <Instagram size={17} />
              </a>
            </div>
          </div>

          <div>
            <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Explore</p>
            <ul className="mt-4 space-y-2.5 text-[0.98rem]">
              <li><Link to="/menu" className="opacity-80 hover:opacity-100">Full Menu</Link></li>
              <li><Link to="/about" className="opacity-80 hover:opacity-100">Our Story</Link></li>
              <li><Link to="/visit" className="opacity-80 hover:opacity-100">Visit & Hours</Link></li>
            </ul>
          </div>

          <div>
            <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Find Us</p>
            <ul className="mt-4 space-y-2.5 text-[0.98rem]" style={{ color: 'color-mix(in srgb, var(--color-ink-inverse) 82%, transparent)' }}>
              <li><a href={SITE.address.mapsUrl} target="_blank" rel="noreferrer" className="hover:underline">{SITE.address.line1}<br />{SITE.address.line2}</a></li>
              <li><a href={SITE.phoneHref} className="hover:underline">{SITE.phone}</a></li>
              <li><a href={SITE.emailHref} className="hover:underline break-all">{SITE.email}</a></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[0.82rem]" style={{ borderTop: '1px solid color-mix(in srgb, var(--color-ink-inverse) 16%, transparent)', color: 'color-mix(in srgb, var(--color-ink-inverse) 60%, transparent)' }}>
          <p>© {new Date().getFullYear()} Parotta Express. All rights reserved.</p>
          <p>Coppell, Texas · South Indian Kitchen</p>
        </div>
      </div>
    </footer>
  );
}
